import React, { createContext, useContext, useEffect, useState } from "react";
import { authApi } from "@/lib/api";
import {
  getUserFromOtpResponse,
  isAdminRole,
  parseOtpMessage,
} from "@/lib/authToken";

const AuthContext = createContext();
const STORAGE_KEY = "omnivaas.adminAuth";
const TOKEN_KEY = "omnivaas.adminToken";
const TOKEN_IAT_KEY = "omnivaas.adminTokenIat";
const TOKEN_EXP_KEY = "omnivaas.adminTokenExp";

function isExpired(exp) {
  return Number(exp || 0) * 1000 <= Date.now();
}

function clearStoredAuth() {
  if (typeof window === "undefined") {
    return;
  }

  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_IAT_KEY);
  sessionStorage.removeItem(TOKEN_EXP_KEY);
  localStorage.removeItem(STORAGE_KEY);
}

function saveStoredAuth(user) {
  if (typeof window === "undefined" || !user?.token) {
    return;
  }

  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ currentUser: user, isAuthenticated: true }),
  );
  sessionStorage.setItem(TOKEN_KEY, user.token);
  sessionStorage.setItem(TOKEN_IAT_KEY, String(user.iat || ""));
  sessionStorage.setItem(TOKEN_EXP_KEY, String(user.exp || ""));
}

function loadStoredAuth() {
  if (typeof window === "undefined") {
    return { currentUser: null, isAuthenticated: false };
  }

  const raw = sessionStorage.getItem(STORAGE_KEY);
  const token = sessionStorage.getItem(TOKEN_KEY);
  const exp = sessionStorage.getItem(TOKEN_EXP_KEY);

  if (!token || !exp || isExpired(exp)) {
    clearStoredAuth();
    return { currentUser: null, isAuthenticated: false };
  }

  if (!raw) {
    return { currentUser: null, isAuthenticated: false };
  }

  try {
    const parsed = JSON.parse(raw);
    const currentUser = parsed.currentUser || null;

    if (!currentUser || !isAdminRole(currentUser.role)) {
      clearStoredAuth();
      return { currentUser: null, isAuthenticated: false };
    }

    return {
      currentUser: {
        ...currentUser,
        token,
        iat: Number(sessionStorage.getItem(TOKEN_IAT_KEY) || currentUser.iat),
        exp: Number(exp),
      },
      isAuthenticated: !!parsed.isAuthenticated,
    };
  } catch {
    clearStoredAuth();
    return { currentUser: null, isAuthenticated: false };
  }
}

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const storedAuth = loadStoredAuth();
  const [currentUser, setCurrentUser] = useState(storedAuth.currentUser);
  const [isAuthenticated, setIsAuthenticated] = useState(
    storedAuth.isAuthenticated,
  );
  const [otpId, setOtpId] = useState(null);
  const [pendingMobile, setPendingMobile] = useState("");

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (currentUser?.token && isAuthenticated) {
      saveStoredAuth(currentUser);
      return;
    }

    clearStoredAuth();
  }, [currentUser, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !currentUser?.exp) {
      return undefined;
    }

    const expiresIn = Number(currentUser.exp) * 1000 - Date.now();

    if (expiresIn <= 0) {
      logout();
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      logout();
    }, expiresIn);

    return () => window.clearTimeout(timeoutId);
  }, [currentUser?.exp, isAuthenticated]);

  const requestOTP = async (mobile) => {
    try {
      const normalizedMobile = String(mobile || "").trim();
      const data = await authApi.requestOtp({
        mobile: normalizedMobile,
        scope: "admin",
      });

      setPendingMobile(normalizedMobile);
      setOtpId(data?.otpId || null);
      return data;
    } catch (error) {
      console.error("requestOTP error in context:", error);
      throw error;
    }
  };

  const authWithOTP = async (code) => {
    let data;

    try {
      data = await authApi.verifyOtp({
        mobile: pendingMobile,
        otp: code,
        otpId,
        scope: "admin",
      });
    } catch {
      throw new Error("Invalid or expired verification code. Please try again.");
    }

    const otpMessage = parseOtpMessage(data?.message);

    if (!otpMessage.isVerificationPassed) {
      throw new Error("Invalid or expired verification code. Please try again.");
    }

    const user = getUserFromOtpResponse(data, pendingMobile);

    if (!isAdminRole(user?.role)) {
      throw new Error("This account does not have admin access.");
    }

    saveStoredAuth(user);
    setCurrentUser(user);
    setIsAuthenticated(true);
    setOtpId(null);
    setPendingMobile("");

    return data;
  };

  const logout = () => {
    clearStoredAuth();
    setCurrentUser(null);
    setIsAuthenticated(false);
    setOtpId(null);
    setPendingMobile("");
  };

  const authenticateWithOtpResponse = (data, identifier = "") => {
    const user = getUserFromOtpResponse(data, identifier);

    if (!isAdminRole(user?.role)) {
      return false;
    }

    saveStoredAuth(user);
    setCurrentUser(user);
    setIsAuthenticated(true);
    setOtpId(null);
    setPendingMobile("");
    return true;
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isAuthenticated,
        requestOTP,
        authWithOTP,
        authenticateWithOtpResponse,
        logout,
        otpId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
